import { useState } from 'react';

import { SelectChangeEvent } from '@mui/material';

import { FEATURES_FLAG_KEYS } from '@/features/featuresFlag/apis/featureFlag.keys';
import { useFeaturesFlagState } from '@/features/featuresFlag/hooks';
import { useGetFoldersAsync, useMutateGetFoldersAsync } from '@/features/folders/FoldersTable/apis/useGetFoldersAsync';
import { FolderItem, ORDER_TYPE } from '@/features/folders/types';
import { useGetReferentialsAsync, REFERENCIALS_URL } from '@/features/shared/apis';
import {
  GetCarrierBorderCrossingServiceAssociationListReferentialResponse,
  GetFoldersStatusReferentialResponse,
} from '@/features/shared/types';

import { useFoldersTableOrderAndPaginationState } from './useFoldersTableOrderAndPagination.state';

export function useFoldersTable() {
  const { featuresFlag } = useFeaturesFlagState();
  const { order, pagination, updateOrderBy, updatePagination, updateTotal } = useFoldersTableOrderAndPaginationState();
  const [folders, setFolders] = useState<FolderItem[]>([]);

  const { data: foldersStatusReferential, isLoading: isGetFoldersStatusReferentialLoading } =
    useGetReferentialsAsync<GetFoldersStatusReferentialResponse>(REFERENCIALS_URL.FOLDERS_STATUS);

  const { isLoading: isGetCarrierBorderCrossingServiceAssociationReferentialLoading } =
    useGetReferentialsAsync<GetCarrierBorderCrossingServiceAssociationListReferentialResponse>(
      REFERENCIALS_URL.CARRIER_BORDER_CROSSING_ASSOCIATION_TYPOLOGY,
    );

  const { isLoading: isGetFoldersLoading } = useGetFoldersAsync(
    {
      order,
      pagination: { page: (pagination.page - 1) * pagination.pageSize, pageSize: pagination.pageSize },
    },
    {
      isEnabled: !folders.length,
      onSuccess: ({ folders: foldersResponse = [], foldersCount }) => {
        setFolders(foldersResponse);
        updateTotal(foldersCount);
      },
    },
  );

  const { mutate: getFolders } = useMutateGetFoldersAsync();

  const handleChangePageSize = (event: SelectChangeEvent<number>) => {
    const newPageSize = +event.target.value;

    getFolders(
      {
        order,
        pagination: { page: 0, pageSize: newPageSize },
      },
      {
        onSuccess: ({ data: { value: foldersResponse = [], '@odata.count': foldersCount } }) => {
          setFolders(foldersResponse);
          updateTotal(foldersCount);
          updatePagination({
            page: 1,
            pageSize: newPageSize,
          });
        },
      },
    );
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    getFolders(
      {
        order,
        pagination: { page: (newPage - 1) * pagination.pageSize, pageSize: pagination.pageSize },
      },
      {
        onSuccess: ({ data: { value: foldersResponse = [], '@odata.count': foldersCount } }) => {
          updateTotal(foldersCount);
          setFolders(foldersResponse);
          updatePagination({ ...pagination, page: newPage });
        },
      },
    );
  };

  const handleSortByCrossingDate = () => {
    const newOrderType = order.type === ORDER_TYPE.DESC ? ORDER_TYPE.ASC : ORDER_TYPE.DESC;
    const newSelectedOrder = { ...order, type: newOrderType };
    getFolders(
      {
        pagination: {
          page: 0,
          pageSize: pagination.pageSize,
        },
        order: newSelectedOrder,
      },
      {
        onSuccess: ({ data: { value: foldersResponse = [], '@odata.count': foldersCount } }) => {
          updateOrderBy(newSelectedOrder);
          setFolders(foldersResponse);
          updateTotal(foldersCount);
          updatePagination({
            page: 1,
            pageSize: pagination.pageSize,
          });
        },
      },
    );
  };

  const getFolderStatusById = (folderStatusId: string) =>
    foldersStatusReferential?.folderStatusResponseList?.find((status) => status.id === folderStatusId);

  const isBpeEnabled = featuresFlag.get(FEATURES_FLAG_KEYS.BPE);
  const isSherpassEnabled = featuresFlag.get(FEATURES_FLAG_KEYS.SHERPASS);

  return {
    isGetCarrierBorderCrossingServiceAssociationReferentialLoading,
    isGetFoldersStatusReferentialLoading,
    isGetFoldersLoading,
    isSherpassEnabled,
    isBpeEnabled,
    folders,
    order,
    handleChangePage,
    getFolderStatusById,
    handleChangePageSize,
    handleSortByCrossingDate,
  };
}
